import { axiosServiceNowApi } from 'services/Api'
import { Dispatch } from 'redux'
import { AxiosResponse, AxiosError } from 'axios'
import { UserAction, ResultUserGetCount, ResultUserGetById, 
    ResultUserGetAllPaginate, ResultUsersManagers, IResultUserGet, User } from 'types/user'
import { AuthenticationAction } from 'types/autentication'
import { ROUTES_NAMES } from 'constants/servicenow.routes'
import { REDUX } from 'constants/'
import store from 'store'
import { applyAuthorizationHeader } from 'helper/getAuthHeader'

const UserActions = {
    /**
     * Executa dois webservices no serviceNow, ws para lista paginada de usuários e ws para contagem de usários totais
     * @param dispatch dispatch padrão para action user
     * @param offset numero atual da pagina solicitada
     * @param limit limite de itens por página
     */
    getAllUsersV2: async (
        dispatch: Dispatch<UserAction>,
        offset?: string,
        limit?: string,
        user?: User,
        order?: any
    ): Promise<string> => {
        const response: Promise<AxiosResponse<ResultUserGetAllPaginate>> =
            axiosServiceNowApi?.get(
                ROUTES_NAMES.user.get.all_paginate_v2(
                    offset,
                    limit,
                    user,
                    order
                )
            )
        await response
            .then((res: AxiosResponse<ResultUserGetAllPaginate>) => {
                dispatch({
                    type: REDUX.reducers.GET_ALL_USERS_COUNT,
                    payload: res?.data.stats.count
                } as UserAction)
                dispatch({
                    type: REDUX.reducers.GET_ALL_USERS_V2,
                    payload: res?.data.result
                } as UserAction)
            })
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_USERS,
                    payload: error.message
                } as UserAction)
            })
            .finally(() => {})
        return ROUTES_NAMES.user.get.all_paginate_v2(offset, limit, user, order)
    },
    getAllUsers: async (
        dispatch: Dispatch<UserAction>,
        offset?: string,
        limit?: string,
        user?: User,
        order?: any
    ): Promise<string> => {
        const responseUsersToCount: Promise<AxiosResponse<ResultUserGetCount>> =
            axiosServiceNowApi?.get(ROUTES_NAMES.user.get.count(user))
        const responseUsersList: Promise<
            AxiosResponse<ResultUserGetAllPaginate>
        > = axiosServiceNowApi?.get(
            ROUTES_NAMES.user.get.all_paginate(offset, limit, user, order)
        )
        await Promise.all([responseUsersToCount, responseUsersList])
            .then(
                (
                    promisseResponse: [
                        AxiosResponse<ResultUserGetCount>,
                        AxiosResponse<ResultUserGetAllPaginate>
                    ]
                ) => {
                    dispatch({
                        type: REDUX.reducers.GET_ALL_USERS_COUNT,
                        payload: promisseResponse[0].data.result.stats.count
                    } as UserAction)
                    dispatch({
                        type: REDUX.reducers.GET_ALL_USERS,
                        payload: promisseResponse[1].data.result
                    } as UserAction)
                }
            )
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_USERS,
                    payload: error
                } as UserAction)
            })
            .finally(() => {})
        return ROUTES_NAMES.user.get.all_paginate(offset, limit)
    },
    getUserByIdResource: async (
        dispatch: Dispatch<UserAction>,
        userId: string
    ): Promise<string> => {
        const response: Promise<AxiosResponse<ResultUserGetById>> =
            axiosServiceNowApi?.get(ROUTES_NAMES.user.get.byidResource(userId))
        await response
            .then((res: AxiosResponse<ResultUserGetById>) => {
                dispatch({
                    type: REDUX.reducers.GET_CURRENT_USER,
                    payload: res.data
                } as UserAction)
            })
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_USERS,
                    payload: error.message
                } as UserAction)
            })
            .finally(() => {})
        return ROUTES_NAMES.user.get.byidResource(userId)
    },
    getUserById: async (
        dispatch: Dispatch<UserAction>,
        userId: string
    ): Promise<string> => {
        const response: Promise<AxiosResponse<ResultUserGetById>> =
            axiosServiceNowApi?.get(ROUTES_NAMES.user.get.byid(userId))
        await response
            .then((res: AxiosResponse<ResultUserGetById>) => {
                dispatch({
                    type: REDUX.reducers.GET_CURRENT_USER,
                    payload: res.data.result[0]
                } as UserAction)
            })
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_USERS,
                    payload: error.message
                } as UserAction)
            })
            .finally(() => {})
        return ROUTES_NAMES.user.get.byid(userId)
    },
    getByAttribute: async (
        dispatch: Dispatch<UserAction>,
        attribute: string,
        value: string
    ): Promise<string> => {
        const response: Promise<AxiosResponse<ResultUserGetById>> =
            axiosServiceNowApi?.get(
                ROUTES_NAMES.user.get.byAttribute(attribute, value)
            )
        await response
            .then((res: AxiosResponse<ResultUserGetById>) => {
                dispatch({
                    type: REDUX.reducers.GET_CURRENT_USER,
                    payload: res.data.result[0]
                } as UserAction)
            })
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_USERS,
                    payload: error.message
                } as UserAction)
            })
            .finally(() => {})
        return ROUTES_NAMES.user.get.byAttribute(attribute, value)
    },
    clearCurrentUser: (dispatch: Dispatch<UserAction>): boolean => {
        dispatch({
            type: REDUX.reducers.GET_USER_BY_ACCOUNT_NAME,
            payload: null
        } as AuthenticationAction) //apagar a busca de usuario por saAccountName
        dispatch({
            type: REDUX.reducers.GET_CURRENT_USER,
            payload: null
        } as UserAction) //apagar usuario no reducer
        return true
    },
    getPaginatedUsersWithLocationsAndSectors: async (
        dispatch: Dispatch<UserAction>,
        offset?: string,
        limit?: string,
        user?: User,
        order?: any
    ): Promise<string> => {
        const ROUTER =
            ROUTES_NAMES.user.get.paginated_users_with_locations_and_sectors(
                offset,
                limit,
                user,
                order
            )
        const response: Promise<AxiosResponse<ResultUserGetAllPaginate>> =
            axiosServiceNowApi?.get(ROUTER)
        await response
            .then((res: AxiosResponse<ResultUserGetAllPaginate>) => {
                dispatch({
                    type: REDUX.reducers.GET_ALL_USERS_COUNT,
                    payload: res?.data.stats.count
                } as UserAction)
                dispatch({
                    type: REDUX.reducers
                        .GET_PAGINATED_USERS_WITH_LOCATIONS_AND_SECTORS,
                    payload: res?.data.result
                } as UserAction)
            })
            .catch((error: AxiosError) => {
                dispatch({
                    type: REDUX.reducers.ERROR_USERS,
                    payload: error.message
                } as UserAction)
            })
            .finally(() => {})
        return ROUTER
    },
    getUsersToDownload: async (user?: User): Promise<string> => {
        const headers: Record<string, string> = {}
        applyAuthorizationHeader(headers)
        const options = { headers }

        store.dispatch({type: REDUX.reducers.SHOW_LOADING,payload: null})

        return fetch(
            `${
                process.env.REACT_APP_PROXY_URL
            }${ROUTES_NAMES.user.get.download_all_users_with_locations_and_sectors(
                user
            )}`,
            options
        )
            .then((res) => {
                return res.json()
            })
            .then((json) => {
                let now = new Date()
                let time = [now.getHours(), now.getMinutes(), now.getSeconds()]
                let filename = `[${now.toJSON().slice(0, 10)}-${time.join(
                    '-'
                )}]_${'users_download'.toLowerCase().replace(' ', '_')}.csv`
                
                let csv = decodeURIComponent(atob(json?.data))
                const blob = new Blob([csv], { type: 'text/csv' })
                const link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.download = filename
                link.click()
                store.dispatch({type: REDUX.reducers.HIDE_LOADING,payload: null})
                return ROUTES_NAMES.user.get.download_all_users_with_locations_and_sectors(user)
            })
            .catch((error: AxiosError) => {
                store.dispatch({
                    type: REDUX.reducers.HIDE_LOADING,
                    payload: error.message
                })
                throw error
            })
            .finally(() => {
                store.dispatch({type: REDUX.reducers.HIDE_LOADING,payload: null})
            })
    }
}
export default UserActions